import { useCallback, useEffect, useState } from 'react';
import {
  listRecordings,
  deleteRecording,
  formatDuration,
  type ZCallRecording,
} from '@/lib/zcall';
import { formatFileSize } from '@/lib/seaweedfs';

interface Props {
  userId: number;
  /** Affiche aussi le panneau (avec état vide) même sans enregistrement. */
  alwaysShow?: boolean;
}

/**
 * Historique des enregistrements de réunions ZCall.
 *
 * Les fichiers sont déposés dans le dossier « ZCall » de Mes médias ; ici on
 * affiche en plus la DURÉE et la TAILLE de chaque enregistrement, avec accès
 * direct à la lecture et au lien de partage.
 */
export default function ZCallRecordingsPanel({ userId, alwaysShow = false }: Props) {
  const [items, setItems] = useState<ZCallRecording[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await listRecordings(userId));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (id: number) => {
    setBusyId(id);
    await deleteRecording(id);
    setBusyId(null);
    void load();
  };

  const copy = (item: ZCallRecording) => {
    void navigator.clipboard.writeText(item.url).then(() => {
      setCopiedId(item.id);
      window.setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('fr-FR', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  if (loading) return null;
  if (items.length === 0 && !alwaysShow) return null;

  const totalSize = items.reduce((sum, i) => sum + (i.size_bytes || 0), 0);
  const totalDuration = items.reduce((sum, i) => sum + (i.duration_seconds || 0), 0);

  return (
    <div className="mb-6 bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-background-200/70">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
            <i className="ri-record-circle-line text-accent-600"></i>
          </span>
          <div>
            <h3 className="text-sm font-semibold text-foreground-900">Enregistrements ZCall</h3>
            <p className="text-[11px] text-foreground-500">
              {items.length} enregistrement{items.length > 1 ? 's' : ''}
              {items.length > 0 && ` · ${formatDuration(totalDuration)} · ${formatFileSize(totalSize)}`}
            </p>
          </div>
        </div>
        <button
          onClick={load}
          className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-600 cursor-pointer transition-colors"
          title="Rafraîchir"
        >
          <i className="ri-refresh-line"></i>
        </button>
      </div>

      {items.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <i className="ri-movie-line text-3xl text-foreground-300"></i>
          <p className="text-sm text-foreground-500 mt-2">Aucun enregistrement pour le moment.</p>
          <p className="text-[11px] text-foreground-400 mt-1">
            Lancez un enregistrement depuis une salle ZCall : le fichier apparaîtra ici.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-background-200/70">
          {items.map((item) => (
            <div key={item.id} className="flex items-center gap-3 px-4 py-3 hover:bg-background-100/50 transition-colors">
              <span className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-play-circle-line text-lg text-accent-600"></i>
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground-900 truncate">{item.title || 'Réunion ZCall'}</p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="text-[11px] text-foreground-400">{formatDate(item.created_at)}</span>
                  <span className="text-[11px] text-foreground-300">·</span>
                  <span className="text-[11px] text-foreground-500 flex items-center gap-1">
                    <i className="ri-time-line"></i>{formatDuration(item.duration_seconds)}
                  </span>
                  <span className="text-[11px] text-foreground-300">·</span>
                  <span className="text-[11px] text-foreground-500 flex items-center gap-1">
                    <i className="ri-hard-drive-3-line"></i>{formatFileSize(item.size_bytes || 0)}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
                  title="Lire l'enregistrement"
                >
                  <i className="ri-play-line text-sm"></i>
                </a>
                <button
                  onClick={() => copy(item)}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
                  title="Copier le lien"
                >
                  <i className={`text-sm ${copiedId === item.id ? 'ri-check-line text-accent-500' : 'ri-file-copy-line'}`}></i>
                </button>
                <button
                  onClick={() => remove(item.id)}
                  disabled={busyId === item.id}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer disabled:opacity-50"
                  title="Supprimer"
                >
                  <i className={`text-sm ${busyId === item.id ? 'ri-loader-4-line animate-spin' : 'ri-delete-bin-line'}`}></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}